import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
// eslint-disable-next-line no-unused-vars
import Chart from 'chart.js/auto';
import Loader from '../../../Components/utils/Loader';
import { CSVLink } from 'react-csv';
import { FiDownload } from 'react-icons/fi';
import { exportToPDF } from '../../../utils/exportUtils';

const API = process.env.REACT_APP_Backend_Host_Name;

const ACTIVITY_CONFIG = {
    signup: { icon: '👤', color: '#6366f1', label: 'Signup' },
    order: { icon: '🛒', color: '#0ea5e9', label: 'Order' },
    listing: { icon: '📦', color: '#10b981', label: 'Listing' },
    dispute: { icon: '⚠️', color: '#f97316', label: 'Dispute' },
};

function AnalyticsActivity({ query }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/api/v1/settings/admin/analytics/activity?${query}`, { headers: { Authorization: `Admin ${token}` } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [query, token]);

    if (loading) return <Loader type="simpleMax" />;
    if (!data) return null;

    const chartOpts = { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 11 } } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 } }, beginAtZero: true } } };

    const hourlyChart = {
        labels: data.hourlyActivity.map(h => `${String(h.hour).padStart(2, '0')}:00`),
        datasets: [{ label: 'Activity', data: data.hourlyActivity.map(h => h.count), backgroundColor: data.hourlyActivity.map(h => { const max = Math.max(...data.hourlyActivity.map(x => x.count)); return h.count === max ? '#6366f1' : 'rgba(99,102,241,0.3)'; }), borderRadius: 4, barThickness: 16 }]
    };

    const dailyChart = {
        labels: data.dailyActivity.map(d => d.day),
        datasets: [{ label: 'Activity', data: data.dailyActivity.map(d => d.count), backgroundColor: data.dailyActivity.map(d => { const max = Math.max(...data.dailyActivity.map(x => x.count)); return d.count === max ? '#10b981' : 'rgba(16,185,129,0.3)'; }), borderRadius: 4, barThickness: 30 }]
    };

    const filteredActivities = typeFilter === 'all' ? data.activities : data.activities.filter(a => a.type === typeFilter);

    const peakHour = data.hourlyActivity.reduce((p, c) => c.count > p.count ? c : p, { hour: 0, count: 0 });
    const peakDay = data.dailyActivity.reduce((p, c) => c.count > p.count ? c : p, { day: '-', count: 0 });

    const formatTime = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    const csvData = data ? [
        { Date: 'Date', Type: 'Activity Type', Message: 'Message', Detail: 'Additional Detail', Amount: 'Amount' },
        ...data.activities.map(a => ({
            Date: formatTime(a.date),
            Type: ACTIVITY_CONFIG[a.type]?.label || a.type,
            Message: a.message,
            Detail: a.detail || '',
            Amount: a.amount ? `$${a.amount.toFixed(2)}` : ''
        }))
    ] : [];

    return (
        <div className="analyticsActivityTab" id="analytics-activity-report">
            <div className="analyticsExportBar">
                <CSVLink data={csvData} filename="activity_log_report.csv" className="exportBtn csvBtn">
                    <FiDownload /> Export CSV
                </CSVLink>
                <button onClick={() => exportToPDF('analytics-activity-report', 'activity_log_report')} className="exportBtn pdfBtn">
                    <FiDownload /> Export PDF
                </button>
            </div>
            <div className="analyticsSimpleStats fourCol">
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#6366f1' }}>{data.activities.length}</div><div className="simpleStatLabel">Total Activities</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#0ea5e9' }}>{String(peakHour.hour).padStart(2, '0')}:00</div><div className="simpleStatLabel">Peak Hour</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#10b981' }}>{peakDay.day}</div><div className="simpleStatLabel">Most Active Day</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#f59e0b' }}>{data.activities.filter(a => a.type === 'signup').length}</div><div className="simpleStatLabel">New Signups</div></div>
            </div>

            <div className="analyticsGridRow two">
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Activity by Hour of Day</h3>
                    <div className="chartContainer" style={{ height: 260 }}><Bar data={hourlyChart} options={chartOpts} /></div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Activity by Day of Week</h3>
                    <div className="chartContainer" style={{ height: 260 }}><Bar data={dailyChart} options={chartOpts} /></div>
                </div>
            </div>

            <div className="analyticsChartCard activityFeedCardFull">
                <div className="chartCardHeaderRow">
                    <h3 className="chartCardTitle">Activity Feed</h3>
                    <div className="miniToggle">
                        <button className={typeFilter === 'all' ? 'active' : ''} onClick={() => setTypeFilter('all')}>All</button>
                        {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => (
                            <button key={key} className={typeFilter === key ? 'active' : ''} onClick={() => setTypeFilter(key)}>{cfg.label}</button>
                        ))}
                    </div>
                </div>
                <div className="analyticsActivityFeed full">
                    {filteredActivities.map((a, i) => {
                        const cfg = ACTIVITY_CONFIG[a.type] || ACTIVITY_CONFIG.signup;
                        return (
                            <div key={i} className="activityItem">
                                <span className="activityIcon" style={{ backgroundColor: `${cfg.color}12`, color: cfg.color }}>{cfg.icon}</span>
                                <div className="activityBody">
                                    <div className="activityMsg">{a.message}</div>
                                    {a.detail && <div className="activityDetail">{a.detail}</div>}
                                    {a.amount > 0 && <div className="activityDetail">${a.amount.toFixed(2)}</div>}
                                </div>
                                <div className="activityTime">{formatTime(a.date)}</div>
                            </div>
                        );
                    })}
                    {filteredActivities.length === 0 && <div className="noDataMsg">No activity found for this period</div>}
                </div>
            </div>
        </div>
    );
}

export default AnalyticsActivity;
